/**
 * Get-started funnel tracking: same event names to Clarity, GA4 (dataLayer), and PostHog.
 * Events are namespaced with get_started_ so they don't collide with other flows
 * (e.g. a future generic "plan_selected" elsewhere).
 *
 * Full event names sent to Clarity + GA4 + PostHog:
 * - get_started_survey_started
 * - get_started_survey_<questionId>_answered (e.g. get_started_survey_skin_tone_answered)
 * - get_started_survey_completed
 * - get_started_account_created
 * - get_started_plan_selected       { plan_id, plan_type: 'annual'|'monthly' }
 * - get_started_checkout_initiated  { plan_type: 'annual'|'monthly', offer_type: 'discount'|'free_gift' }
 */

import posthog from 'posthog-js'

import { trackClarityEvent } from '@lib/analytics/clarity'
import { gtmEvent } from '@lib/analytics/gtm'

const FUNNEL_NAME = 'get_started'

/** Prefix for all get-started events so they're easy to filter and won't conflict with other flows. */
const EVENT_PREFIX = 'get_started_'

/**
 * Maps key get-started events to neutral equivalents fired to PostHog only.
 * These neutral events are also fired by the control path (SurveyAuthModal, PricingCard)
 * with flow: 'control', enabling fair A/B comparison in the experiment secondary metrics.
 */
const NEUTRAL_EVENT_MAP: Record<string, string> = {
	get_started_survey_started: 'subscription_survey_started',
	get_started_survey_completed: 'subscription_survey_completed',
	get_started_plan_selected: 'subscription_plan_selected',
}

export type GetStartedEventParams = {
	question_id?: string
	[key: string]: string | number | boolean | undefined
}

/**
 * Fire a get-started funnel event to Clarity, GA4 (dataLayer), and PostHog.
 * Prepends get_started_ to the event name so events are namespaced (e.g. get_started_plan_selected).
 */
export function trackGetStartedEvent(
	eventName: string,
	params?: GetStartedEventParams
): void {
	const fullEventName = eventName.startsWith(EVENT_PREFIX)
		? eventName
		: `${EVENT_PREFIX}${eventName}`
	trackClarityEvent(fullEventName)
	const cleanParams = params
		? Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
		: {}
	gtmEvent({
		event: fullEventName,
		data: { funnel: FUNNEL_NAME, ...cleanParams }
	})
	posthog.capture(fullEventName, { funnel: FUNNEL_NAME, ...cleanParams })

	// Also fire the neutral parallel event (if one exists) so the A/B experiment
	// secondary metrics can fairly compare control and variant paths.
	const neutralEvent = NEUTRAL_EVENT_MAP[fullEventName]
	if (neutralEvent) {
		posthog.capture(neutralEvent, { flow: 'variant', ...cleanParams })
	}
}
